import { useTranslate } from '@tolgee/react';
import { Button, TableColumnsType, TableColumnType, TableProps, Tag, Tooltip } from 'antd';
import ScrollTable from 'components/ScrollTable';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { StarRate } from 'views/Review/components/Profile';
import { ReactComponent as ThreeSvg } from './assets/haoping.svg';
import { ReactComponent as TwoSvg } from './assets/yiban.svg';
import { ReactComponent as OneSvg } from './assets/chaping.svg';
import Icon from '@ant-design/icons';
import dayjs from 'dayjs';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { useMemo } from 'react';

export function useLabels() {
  const { t } = useTranslate();

  const scoreSourceLabelMap = useMemo(() => {
    return {
      INTELLIGENT_INVITE: t('智能邀评'),
      OTHER: t('OTHER'),
    };
  }, [t]);

  const receptionModeLabelMap = useMemo(() => {
    return {
      BOT: t('机器人'),
      HUMAN: t('人工'),
      HYBRID: t('人机协同'),
      OFFICIAL: t('官方接待'),
      NONE: t('未接待'),
    };
  }, [t]);

  return {
    t,
    scoreSourceLabelMap,
    receptionModeLabelMap,
  };
}

type RT = Dashboard.PlatformData.CsatData;

function CsatDetailTable({
  platform,
  onCheckConversation,
  ...otherProps
}: TableProps<RT> & {
  platform: General.PlatformUpperCase;
  onCheckConversation: (record: RT) => void;
}) {
  const { t, scoreSourceLabelMap, receptionModeLabelMap } = useLabels();

  const { config } = useStoreNameConfig();

  const columns: TableColumnsType<RT> = [
    {
      key: 'region',
      dataIndex: 'region',
      title: t('站点'),
      width: 60,
    },
    {
      key: 'storeName',
      dataIndex: 'storeName',
      title: t('店铺名称'),
      width: 160,
      render: (_, { storeAlias, storeName, region }) =>
        isStoreAliasFirst(config, platform, region) ? storeAlias || storeName : storeName,
    },
    {
      key: 'buyerNick',
      dataIndex: 'buyerNick',
      title: t('买家昵称'),
      width: 160,
    },
    {
      key: 'csatScore',
      dataIndex: 'csatScore',
      title: t('reviewSetting.评分'),
      width: 120,
      render: (score) => {
        if (isTiKTokUpperCase(platform)) {
          return <StarRate disabled value={score} />;
        }
        if (score === 2) {
          return (
            <Tag color="green">
              <Icon component={ThreeSvg} />
              {t('review.好评')}
            </Tag>
          );
        }
        if (score === 1) {
          return (
            <Tag color="warning">
              <Icon component={TwoSvg} />
              {t('一般')}
            </Tag>
          );
        }
        if (score === 0) {
          return (
            <Tag color="error">
              <Icon component={OneSvg} />
              {t('aide.差评')}
            </Tag>
          );
        }
        return '-';
      },
    },
    ...(isTiKTokUpperCase(platform)
      ? [
          {
            key: 'dissatisfactionReason',
            dataIndex: 'dissatisfactionReason',
            title: (
              <div>
                {t('评价原因')}
                <Tooltip title={t('cast_评价原因_tips')}>
                  <What />
                </Tooltip>
              </div>
            ),
            width: 240,
            render: (dissatisfactionReason: RT['dissatisfactionReason']) =>
              dissatisfactionReason
                ? t(`dissatisfactionReason.${dissatisfactionReason}`, dissatisfactionReason)
                : '-',
          } as TableColumnType<RT>,
        ]
      : []),
    {
      key: 'scoreTimeStamp',
      dataIndex: 'scoreTimeStamp',
      title: t('评分时间'),
      width: 160,
      render: (time: RT['scoreTimeStamp']) => dayjs(Number(time)).format('YYYY/MM/DD HH:mm'),
    },
    {
      key: 'scoreSource',
      dataIndex: 'scoreSource',
      title: t('评分来源'),
      width: 140,
      render: (scoreSource: RT['scoreSource']) => <Tag>{scoreSourceLabelMap[scoreSource]}</Tag>,
    },
    {
      key: 'receptionMode',
      dataIndex: 'receptionMode',
      title: (
        <div>
          {t('接待方式')}
          <Tooltip title={<div className="whitespace-pre-wrap">{t('csat_接待方式_tips')}</div>}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 140,
      render: (receptionMode: RT['receptionMode']) => receptionModeLabelMap[receptionMode],
    },
    {
      key: 'lastReceptionAssistantName',
      dataIndex: 'lastReceptionAssistantName',
      title: (
        <div>
          {t('最后接待客服')}
          <Tooltip title={t('csat_最后接待客服_tips')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 150,
    },
    {
      key: 'operate',
      title: t('操作'),
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => onCheckConversation(record)}>
          {t('查看会话')}
        </Button>
      ),
    },
  ];

  return <ScrollTable columns={columns} {...otherProps} />;
}

export default CsatDetailTable;
